import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../store';
import { getCurrentUser } from '../api';

const API_BASE = import.meta.env.VITE_API_URL || '';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No verification token provided.');
      return;
    }

    const controller = new AbortController();

    fetch(`${API_BASE}/auth/verify-email?token=${encodeURIComponent(token)}`, {
      signal: controller.signal,
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok && data.success) {
          setStatus('success');
          // Quiet refresh of the user — NOT checkAuth(), which flips the
          // global isLoading flag and causes App to unmount Routes. That
          // triggered a remount of this page, a second fetch, and an
          // infinite loop once the backend became idempotent.
          getCurrentUser()
            .then((user) => {
              if (user) useAuthStore.getState().setUser(user);
            })
            .catch(() => {
              // ignore; success UI already shown
            });
          return;
        }
        setStatus('error');
        setErrorMessage(data.error || 'Verification failed.');
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        setStatus('error');
        setErrorMessage('Network error. Please try again.');
      });

    return () => controller.abort();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-dark-900 p-4">
      <div className="bg-dark-800 border border-dark-600 rounded-xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <div className="spinner w-8 h-8 mx-auto mb-4" />
            <p className="text-dark-200">Verifying your email...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-900/50 flex items-center justify-center">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-dark-50 mb-2">Email Verified</h1>
            <p className="text-dark-300 mb-6">Your email has been verified. You now have full access to all features.</p>
            <Link to="/" className="btn-primary inline-block">Go to Dashboard</Link>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-900/50 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-dark-50 mb-2">Verification Failed</h1>
            <p className="text-dark-300 mb-6">{errorMessage}</p>
            <Link to="/" className="btn-secondary inline-block">Go to Dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
